import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Trophy, Medal, Users } from 'lucide-react';
import { championshipApi, publicApi } from '../lib/api';
import { Button } from '../components/ui/button';
import SearchableCombobox from '../components/SearchableCombobox';

// Accent-insensitive helper so typing "pre" matches "PRÉ-MIRIM".
const normalizeForSearch = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// A ordem das categorias vem do SERVIDOR (IDADE -> FAIXA -> PESO -> SEXO ->
// MODALIDADE, cada linha traz `ordem`). Aqui só se obedece; dados antigos, sem
// esse campo, continuam caindo na ordem alfabética de antes.
const compararOrdem = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return 0;
  const tamanho = Math.max(a.length, b.length);
  for (let i = 0; i < tamanho; i += 1) {
    const va = a[i];
    const vb = b[i];
    if (va === vb) continue;
    if (typeof va === 'number' && typeof vb === 'number') return va - vb;
    return String(va ?? '').localeCompare(String(vb ?? ''));
  }
  return 0;
};

const compararCategorias = (ordemA, nomeA, ordemB, nomeB) => {
  const porOrdem = compararOrdem(ordemA, ordemB);
  return porOrdem !== 0 ? porOrdem : String(nomeA || '').localeCompare(String(nomeB || ''));
};

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-6 py-2 rounded-full text-sm font-medium transition-all border ${
      active
        ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
        : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#D4AF37]/40 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const placeBadge = (place) => {
  const map = {
    1: { bg: 'bg-[#D4AF37]', txt: 'text-black', label: '🥇 1º' },
    2: { bg: 'bg-[#C0C0C0]', txt: 'text-black', label: '🥈 2º' },
    3: { bg: 'bg-[#CD7F32]', txt: 'text-black', label: '🥉 3º' },
  };
  const s = map[place] || { bg: 'bg-[#27272A]', txt: 'text-[#A1A1AA]', label: `${place}º` };
  return (
    <span className={`inline-flex items-center justify-center min-w-[40px] px-2 py-0.5 rounded-full text-xs font-bold ${s.bg} ${s.txt}`}>
      {s.label}
    </span>
  );
};

export default function PublicRankingPage() {
  const { id } = useParams();
  const [championship, setChampionship] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('athletes');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [rankingModality, setRankingModality] = useState('all'); // all | gi | nogi

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, t] = await Promise.all([
        championshipApi.get(id),
        publicApi.getLiveRanking(id, ['gi', 'nogi'].includes(rankingModality) ? rankingModality : undefined),
        publicApi.getTeamRanking(id, ['gi', 'nogi'].includes(rankingModality) ? rankingModality : undefined),
      ]);
      setChampionship(c.data);
      setAthletes(a.data || []);
      setTeams(t.data || []);
    } catch (e) {
      setChampionship(null);
    } finally {
      setLoading(false);
    }
  }, [id, rankingModality]);

  useEffect(() => { load(); }, [load]);

  // Ranking separado por modalidade (combine=false): comeca em "Kimono"
  useEffect(() => {
    if (championship?.modality === 'misto' &&
        championship?.combine_modality_ranking === false &&
        rankingModality === 'all') {
      setRankingModality('gi');
    }
  }, [championship, rankingModality]);

  const categories = useMemo(() => {
    const primeira = new Map();
    for (const a of athletes) {
      if (a.category_name && !primeira.has(a.category_name)) primeira.set(a.category_name, a);
    }
    return Array.from(primeira.keys()).sort((x, y) =>
      compararCategorias(primeira.get(x)?.ordem, x, primeira.get(y)?.ordem, y));
  }, [athletes]);

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Todas as categorias' },
      ...categories.map((c) => ({ value: c, label: c })),
    ],
    [categories],
  );

  const groupedAthletes = useMemo(() => {
    const filtered = categoryFilter ? athletes.filter((a) => a.category_name === categoryFilter) : athletes;
    const map = new Map();
    for (const r of filtered) {
      const key = r.category_name || 'Sem categoria';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries()).sort(([nomeA, listaA], [nomeB, listaB]) =>
      compararCategorias(listaA[0]?.ordem, nomeA, listaB[0]?.ordem, nomeB));
  }, [athletes, categoryFilter]);

  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => b.gold - a.gold || b.silver - a.silver || b.bronze - a.bronze || a.team_name.localeCompare(b.team_name)),
    [teams],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
      </div>
    );
  }
  if (!championship) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#A1A1AA]">Campeonato nao encontrado</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-ranking-page">
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/public/${id}`} className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#D4AF37] transition-colors" data-testid="back-to-championship">
              <ArrowLeft size={16} />
            </Link>
            <div className="min-w-0">
              <p className="text-xs text-[#71717A] uppercase tracking-wider">Ranking ao Vivo</p>
              <h1 className="text-base sm:text-lg font-bold text-white font-['Chivo'] truncate">{championship.name}</h1>
            </div>
          </div>
          <Link to={`/public/${id}`}>
            <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Trophy size={16} className="mr-1.5" /> Pagina do Campeonato
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Medal className="w-6 h-6 text-[#D4AF37]" />
          <h2 className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">Ranking</h2>
          <span className="text-[#A1A1AA] text-sm">
            · {athletes.length} colocacoes · {teams.length} equipes
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mb-5 items-center">
          <TabButton active={mode === 'athletes'} onClick={() => setMode('athletes')}>
            <Users size={16} className="inline mr-1.5" /> Atletas
          </TabButton>
          <TabButton active={mode === 'teams'} onClick={() => setMode('teams')}>
            <Trophy size={16} className="inline mr-1.5" /> Equipes
          </TabButton>
          {championship?.modality === 'misto' && (
            <select
              value={rankingModality}
              onChange={(e) => setRankingModality(e.target.value)}
              className="ml-auto px-3 py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="public-ranking-modality"
            >
              {championship?.combine_modality_ranking !== false && <option value="all">Todas as modalidades</option>}
              <option value="gi">Kimono</option>
              <option value="nogi">No-Gi</option>
            </select>
          )}
        </div>

        {mode === 'athletes' && (
          <>
            {categories.length > 0 && (
              <div className="mb-5">
                <SearchableCombobox
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  options={categoryOptions}
                  placeholder="Todas as categorias"
                  searchPlaceholder="Digitar categoria..."
                  emptyText="Nenhuma categoria encontrada"
                  keywords={(opt) => normalizeForSearch(opt.label)}
                  testId="ranking-category-filter"
                  className="w-full sm:w-[420px]"
                />
              </div>
            )}

            {groupedAthletes.length === 0 ? (
              <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
                <Medal className="w-10 h-10 mx-auto mb-2 opacity-50" />
                Nenhum resultado registrado ainda.
              </div>
            ) : (
              <div className="space-y-5">
                {groupedAthletes.map(([cat, list]) => (
                  <div key={cat}>
                    <div className="flex items-baseline gap-2 mb-2 border-b border-[#27272A] pb-1">
                      <h3 className="text-lg font-bold text-white font-['Chivo']">{cat}</h3>
                      <span className="text-xs text-[#71717A]">{list.length} {list.length === 1 ? 'colocado' : 'colocados'}</span>
                    </div>
                    <div className="rounded-xl overflow-hidden border border-[#27272A]">
                      {list.sort((a, b) => a.place - b.place).map((r, idx) => (
                        <div
                          key={`${r.athlete_id}-${r.place}`}
                          className={`flex items-center gap-4 px-4 py-3 ${idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'}`}
                          data-testid={`ranking-athlete-${r.athlete_id}`}
                        >
                          {placeBadge(r.place)}
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium text-sm truncate">{r.athlete_name}</div>
                            {r.team_name && <div className="text-xs text-[#A1A1AA] truncate">{r.team_name}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {mode === 'teams' && (
          <div className="rounded-xl overflow-hidden border border-[#27272A]" data-testid="ranking-teams">
            <div className="grid grid-cols-[60px_1fr_60px_60px_60px_70px] gap-2 px-4 py-3 bg-[#181818] text-xs uppercase tracking-wider text-[#71717A] font-semibold">
              <div>Pos.</div>
              <div>Equipe</div>
              <div className="text-center">🥇</div>
              <div className="text-center">🥈</div>
              <div className="text-center">🥉</div>
              <div className="text-center">Pontos</div>
            </div>
            {sortedTeams.length === 0 ? (
              <div className="px-4 py-12 text-center text-[#71717A]">Nenhuma equipe pontuada ainda.</div>
            ) : sortedTeams.map((t, idx) => (
              <div key={t.team_id} className={`grid grid-cols-[60px_1fr_60px_60px_60px_70px] gap-2 px-4 py-3 items-center ${idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'}`} data-testid={`ranking-team-${t.team_id}`}>
                <div>{placeBadge(idx + 1)}</div>
                <div className="text-white font-medium text-sm truncate">{t.team_name}</div>
                <div className="text-center medalha-ouro font-bold">{t.gold}</div>
                <div className="text-center medalha-prata font-bold">{t.silver}</div>
                <div className="text-center medalha-bronze font-bold">{t.bronze}</div>
                <div className="text-center text-white font-bold">{t.total_points}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
